import { untrack } from 'svelte'
import { createEventDispatcher } from 'svelte'
import { tick } from 'svelte'
import { detectedEnv } from '#app/lib/env_config'
import { logError } from '#app/lib/loggers'
import type { ClickEvent, KeyDownEvent, KeyUpEvent } from '#app/types/declarations/svelte'

export function BubbleUpComponentEvent (dispatch?: ReturnType<typeof createEventDispatcher>) {
  dispatch = dispatch || createEventDispatcher()
  return e => {
    if (e instanceof CustomEvent) {
      // Re-dispatch an identical custom event
      dispatch(e.type, e.detail)
    } else {
      // 'e' is expected to be a DOM event
      // Dispatch the event object as event detail to make its attributes,
      // such as its `target` accessible to parent components
      dispatch(e.type, e)
    }
  }
}

/**
 * Use as an syntactic sugar around Svelte untrack function, within an $effect:
 * all arguments passed to onChange are tracked, but the final callback isn't,
 * so that it's variables in closure can't trigger effects
 * The last argument MUST thus be a callback function, but the rest parameter can not not be last
 * so it is typed as `unknown`
 */
export function onChange (...args: unknown[]) {
  const callback = args.slice(-1)[0] as () => unknown
  untrack(callback)
}

/**
 * Same as onChange, but the callback is called after Svelte tick
 */
export function onChangeAfterTick (...args: unknown[]) {
  const _args = args.slice(0, -1)
  const callback = args.slice(-1)[0] as () => unknown
  onChange(..._args, () => {
    tick()
    .then(callback)
    .catch(logError)
  })
}

/**
 * A noop function with only purpose to declare which pieces of state
 * should be considered a state dependency by an $effect or $derived rune.
 * Its name is meant to reflect that it has the opposite function of Svelte `untrack`.
 * See https://svelte.dev/docs/svelte/$effect#Understanding-dependencies
 * and https://svelte.dev/docs/svelte/svelte#untrack
*/
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function track (...args: unknown[]) {}

export function initSvelteWarningFilter () {
  if (detectedEnv !== 'prod') {
    const originalWarn = globalThis.console.warn
    globalThis.console.warn = function (...args) {
      if (args[0].includes('ownership_invalid_binding') && args[0].includes('app_layout.svelte')) {
        // Silence ownership_invalid_binding warning about the AppLayout not binding to its children components
        return
      }
      originalWarn(...args)
    }
  }
}

/** Same as svelte stopPropagation, but not deprecated */
export function stopPropagation (event: ClickEvent | KeyDownEvent | KeyUpEvent) {
  event.stopPropagation()
  return event
}
